import React, {useEffect, useMemo, useState} from 'react';
import {FaEraser} from "react-icons/fa";
import {Modal} from "../../../../components/modal/modal.jsx";
import Button from "../../../../components/button/button.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import InputCheckbox from "../../../../components/input/input-checkbox.jsx";
import InputSwitch from "../../../../components/input/input-switch.jsx";
import {normalizeFilterText} from "../../../../utils/normalizeFilterText.js";

const ANALYSABLE_FIELD_TYPES = [
    "input",
    "date",
    "datetime",
    "radio",
    "checkbox",
];

const buildFieldGroups = (structure = []) => {
    const groups = [];
    let current = null;

    structure.forEach((item) => {
        if (item.type === "subtitle" && item.label) {
            current = {id: item.id, title: item.label, items: []};
            groups.push(current);
            return;
        }

        if (!ANALYSABLE_FIELD_TYPES.includes(item.type)) return;

        if (!current) {
            current = {id: "root", title: null, items: []};
            groups.push(current);
        }

        current.items.push({
            id: item.id,
            label: item.label,
            type: item.type
        });
    });

    return groups.filter(({items}) => items.length);
};

const rowClass = (selected, first, last) => {
    const base = "flex w-full cursor-pointer gap-2 py-1 text-left items-center";
    const corners = [first ? "rounded-t" : "", last ? "rounded-b mb-2" : ""].join(" ");

    return selected
        ? `${base} ${corners} border-l-2 border-l-[#063A6C] bg-blue-50 pr-4 pl-[14px]`
        : `${base} ${corners} px-4 hover:bg-[#f9f9f9]`;
};

const FiltersStructureModal = ({open, setOpen, form, value = [], onConfirm, onApply}) => {
    const [search, setSearch] = useState("");
    const [selectedIds, setSelectedIds] = useState([]);

    const groups = useMemo(() => buildFieldGroups(form?.structure), [form]);

    const allItems = useMemo(() => groups.flatMap((group) => group.items), [groups]);
    const selectedItems = useMemo(() => allItems.filter(({id}) => selectedIds.includes(id)), [allItems, selectedIds]);

    useEffect(() => {
        if (!open) return;

        setSelectedIds(value.map(({id}) => id));
        setSearch("");
    }, [open]);

    const normalizedSearch = normalizeFilterText(search);

    const visibleGroups = useMemo(() => {
        const matches = (item) => !(normalizedSearch && !normalizeFilterText(item.label).includes(normalizedSearch));

        return groups
            .map((group) => ({...group, visibleItems: group.items.filter(matches)}))
            .filter(({visibleItems}) => visibleItems.length);
    }, [groups, normalizedSearch]);

    const visibleIds = useMemo(
        () => visibleGroups.flatMap(({visibleItems}) => visibleItems).map(({id}) => id),
        [visibleGroups]
    );

    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));

    const toggleField = (id) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(current => current !== id) : [...prev, id]);
    };

    const selectVisible = () => setSelectedIds(prev => [...new Set([...prev, ...visibleIds])]);

    const unselectVisible = () => setSelectedIds(prev => prev.filter(id => !visibleIds.includes(id)));

    const selectedFields = () => selectedItems.map(({id, label, type}) => ({id, label, type}));

    const confirm = () => {
        onConfirm?.(selectedFields());

        setOpen(false);
    };

    const applyAndClose = () => {
        onApply?.(selectedFields());

        setOpen(false);
    };

    const groupTally = (groupItems) => {
        const selected = groupItems.filter((item) => selectedIds.includes(item.id)).length;

        return `${selected} de ${groupItems.length} ${groupItems.length === 1 ? "marcado" : "marcados"}`;
    };

    return (
        <Modal open={open} setOpen={setOpen} title={"Selecionar campos do relatório"} className={"w-full max-w-6xl"}>
            <Modal.Body className={"flex h-[calc(100vh-180px)] flex-col p-0!"}>
                <div className="flex flex-col gap-2 px-4 pt-3 pb-4">
                    <div className="flex justify-between gap-3">
                        <InputForm
                            id="field-search"
                            type="text"
                            label="Campo"
                            value={search}
                            onChange={({target: {value}}) => setSearch(value)}
                            classNameWrapper="w-[calc(100%-60px)]"
                            isClearable
                        />

                        <div className="buttons">
                            <Button target={"primary-outline"} title={"Limpar busca"} onClick={() => setSearch("")}>
                                <FaEraser/>
                            </Button>
                        </div>
                    </div>
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto px-4">
                    <InputSwitch
                        id="select-all-fields"
                        label="Selecionar todos"
                        className="mb-2"
                        disabled={!visibleIds.length}
                        checked={allVisibleSelected}
                        onChange={(checked) => checked ? selectVisible() : unselectVisible()}
                    />
                    <div className="flex items-center justify-between gap-2 rounded border border-[#ddd] bg-[#f5f5f5] px-4 py-1.5 mb-2">
                        <span className="text-[13px] font-semibold text-[#676a6c]">{form?.label}</span>
                    </div>

                    {
                        !groups.length &&
                        <div className="px-4 py-10 text-center text-sm text-[#676a6c]">
                            Este formulário não possui campos analisáveis.
                        </div>
                    }

                    {
                        !!groups.length && !visibleGroups.length &&
                        <div className="px-4 py-10 text-center text-sm text-[#676a6c]">
                            Nenhum campo encontrado para os filtros aplicados.
                        </div>
                    }

                    {visibleGroups.map((group) => (
                        <div key={group.id}>
                            {
                                group.title &&
                                <div className="flex items-center justify-between gap-2 rounded border border-[#ddd] bg-[#f5f5f5] px-4 py-1.5 mb-2">
                                    <span className="text-[13px] font-semibold text-[#676a6c]">{group.title}</span>
                                    <span className="shrink-0 whitespace-nowrap text-[12px] text-[#676a6c]">
                                        {groupTally(group.items)}
                                    </span>
                                </div>
                            }

                            {group.visibleItems.map((item, index) => {
                                const selected = selectedIds.includes(item.id);
                                const first = index === 0;
                                const last = index === group.visibleItems.length - 1;

                                return (
                                    <label key={item.id} title={item.label} className={rowClass(selected, first, last)}>
                                        <InputCheckbox
                                            id={`field-${item.id}`}
                                            name={`field-${item.id}`}
                                            className="shrink-0"
                                            checked={selected}
                                            onChange={() => toggleField(item.id)}
                                        />
                                        <span className={"min-w-0 flex-1 text-[14px] leading-[1.4] text-[#676a6c]"}>{item.label}</span>
                                    </label>
                                );
                            })}
                        </div>
                    ))}
                </div>
            </Modal.Body>

            <Modal.Footer className={"items-center justify-between!"}>
                <span className="text-[#676a6c]">
                    {selectedItems.length} de {allItems.length} {allItems.length === 1 ? "campo marcado" : "campos marcados"}
                </span>

                <Button
                    target={"primary-outline"}
                    title={"Guardar a seleção sem gerar o relatório"}
                    disabled={!selectedItems.length}
                    onClick={confirm}
                >
                    Confirmar
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default FiltersStructureModal;
